import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReAuth } from '@/store/features/baseRtkQuery';
import type { ChannelAction } from '@/store/features/channel/channelApiSlice';

export type PipelineOutcome =
    | 'replied'
    | 'ticket_created'
    | 'stored_only'
    | 'skipped_unregistered'
    | 'skipped_no_trigger'
    | 'denied_permission'
    | 'security_clean'
    | 'security_ticket_created'
    /** Channels suppressed a duplicate externally visible act (spec R-41). */
    | 'skipped_duplicate'
    | 'failed'
    | 'running'
    | 'notified'
    | 'triaged'
    | 'resolved'
    | 'awaiting_info'
    | 'escalated'
    | 'synced';

export type PipelineStepStatus = 'ok' | 'skipped' | 'failed';

export interface PipelineRunSummary {
    id: string;
    channelUid: string | null;
    channelName: string | null;
    platform: string;
    platformChatId: string | null;
    platformMessageId: string | null;
    userId: string | null;
    senderName: string | null;
    triggerType: string | null;
    outcome: PipelineOutcome;
    lastStep: string | null;
    errorMessage: string | null;
    actionId: string | null;
    actionExternalId: string | null;
    traceId: string | null;
    spanId: string | null;
    startedAt: string;
    finishedAt: string | null;
    durationMs: number | null;
    stepCount: number;
    failedStepCount: number;
}

export interface PipelineStep {
    id: string;
    sequence: number;
    step: string;
    status: PipelineStepStatus;
    summary: string | null;
    detail: string | null;
    data: Record<string, unknown> | null;
    durationMs: number | null;
    at: string;
}

export interface PipelineMessageAttachment {
    type: string;
    url?: string | null;
    fileName?: string | null;
    mimeType?: string | null;
    fileSize?: number | null;
    analysisResult?: string | null;
    processed?: boolean;
}

export interface PipelineMessage {
    id: string;
    channelUid: string;
    platform: string;
    platformMessageId: string | null;
    platformChatId: string | null;
    threadId: string | null;
    replyToMessageId: string | null;
    userId: string | null;
    messageText: string | null;
    attachments: PipelineMessageAttachment[] | null;
    messageDate: string;
    createdAt: string;
    isOutbound: boolean;
}

export interface PipelineToolExecution {
    id: number;
    toolName: string;
    pluginName: string | null;
    status: string;
    arguments: string | null;
    result: string | null;
    error: string | null;
    durationMs: number | null;
    sequenceNumber: number;
    timestamp: string;
}

export interface PipelineAgentMessage {
    id: number;
    chatId: string;
    agent: string | null;
    message: string | null;
    inputTokens: number;
    outputTokens: number;
    time: string;
    toolExecutions: PipelineToolExecution[];
}

export interface PipelineRunDetail {
    run: PipelineRunSummary;
    threadId: string | null;
    eonChatId: string | null;
    eonMessageId: string | null;
    steps: PipelineStep[];
    inboundMessage: PipelineMessage | null;
    outboundMessage: PipelineMessage | null;
    action: ChannelAction | null;
    agentMessage: PipelineAgentMessage | null;
}

export interface PipelineRunListResponse {
    runs: PipelineRunSummary[];
    total: number;
    hasMore: boolean;
    outcomeCounts: Record<string, number>;
}

export interface GetAuditRunsParams {
    channelUid?: string;
    platform?: string;
    /** Single outcome, or comma-separated list for multi-select. */
    outcome?: string;
    search?: string;
    from?: string;
    to?: string;
    limit?: number;
    offset?: number;
}

export const channelAuditApiSlice = createApi({
    reducerPath: 'channelAuditApi',
    baseQuery: baseQueryWithReAuth,
    tagTypes: ['Audit'],
    endpoints: (builder) => ({
        getAuditRuns: builder.query<PipelineRunListResponse, GetAuditRunsParams>({
            query: ({ channelUid, platform, outcome, search, from, to, limit = 50, offset = 0 }) => ({
                url: 'api/channel-audit/runs',
                method: 'GET',
                params: {
                    channelUid: channelUid || undefined,
                    platform: platform || undefined,
                    outcome: outcome || undefined,
                    search: search || undefined,
                    from: from || undefined,
                    to: to || undefined,
                    limit,
                    offset,
                },
            }),
            providesTags: ['Audit'],
        }),
        getAuditRun: builder.query<PipelineRunDetail, string>({
            query: (runId) => ({
                url: `api/channel-audit/runs/${runId}`,
                method: 'GET',
            }),
            providesTags: (_result, _error, runId) => [{ type: 'Audit', id: runId }],
        }),
    }),
});

export const channelAuditApiActions = {
    useGetAuditRunsQuery: channelAuditApiSlice.useGetAuditRunsQuery,
    useGetAuditRunQuery: channelAuditApiSlice.useGetAuditRunQuery,
};

export default channelAuditApiSlice;
